import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Users, DollarSign, Target, CalendarCheck, Loader2, Download, BarChart3, Layout, TrendingUp, Share2,
} from "lucide-react";
import { toast } from "sonner";

const money = (n) => "$" + (n || 0).toLocaleString();
const AGENCY_ROLES = ["agency_owner", "agency_admin"];

function daysAgo(n) {
  const d = new Date(Date.now() - n * 86400000);
  return d.toISOString().slice(0, 10);
}

function Metric({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className="p-5 bg-card border-border/70" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
          <p className="text-2xl sm:text-3xl font-display font-bold mt-2 truncate">{value}</p>
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}22` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
    </Card>
  );
}

export default function Reports() {
  const { activeSubId, activeSub, user } = useAuth();
  const isAgency = AGENCY_ROLES.includes(user?.role);
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const [agency, setAgency] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const brand = activeSub?.branding?.primary_color || "#6366F1";

  const qs = useCallback(() => `from=${from}&to=${to}`, [from, to]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [api.get(`/reports/agency?${qs()}`)];
      if (activeSubId) calls.push(api.get(`/reports/sub-account?sub_account_id=${activeSubId}&${qs()}`));
      const res = await Promise.all(calls);
      setAgency(res[0].data);
      if (activeSubId) setClient(res[1].data);
    } catch (e) {
      toast.error("Failed to load reports");
    }
    setLoading(false);
  }, [qs, activeSubId]);

  useEffect(() => { load(); }, [load]);

  const download = async (kind) => {
    try {
      const url = kind === "agency"
        ? `/reports/agency/export?${qs()}`
        : `/reports/sub-account/export?sub_account_id=${activeSubId}&${qs()}`;
      const { data } = await api.get(url, { responseType: "blob" });
      const href = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = href;
      a.download = kind === "agency" ? "momento-agency-report.csv" : "momento-client-report.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(href);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const shareLink = async (kind) => {
    try {
      const url = kind === "agency"
        ? `/reports/share?scope=agency&from=${from}&to=${to}`
        : `/reports/share?scope=sub&sub_account_id=${activeSubId}&from=${from}&to=${to}`;
      const { data } = await api.post(url);
      await navigator.clipboard?.writeText(data.url);
      toast.success("Shareable link copied — send it to your client");
    } catch { toast.error("Could not create link"); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Analytics</p>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Reports</h1>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-xs">From</Label>
            <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="h-10 w-[150px]" data-testid="report-from" /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label>
            <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="h-10 w-[150px]" data-testid="report-to" /></div>
          <div className="flex gap-1.5">
            {[["7d", 6], ["30d", 29], ["90d", 89]].map(([lbl, n]) => (
              <Button key={lbl} variant="outline" size="sm" className="h-10" onClick={() => { setFrom(daysAgo(n)); setTo(daysAgo(0)); }} data-testid={`preset-${lbl}`}>{lbl}</Button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <Tabs defaultValue={isAgency ? "agency" : "client"}>
          <TabsList>
            {isAgency && <TabsTrigger value="agency" data-testid="tab-agency">Agency roll-up</TabsTrigger>}
            <TabsTrigger value="client" data-testid="tab-client">{activeSub?.name || "This client"}</TabsTrigger>
          </TabsList>

          {isAgency && (
            <TabsContent value="agency" className="mt-6 space-y-6">
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="gap-2" onClick={() => shareLink("agency")} data-testid="share-agency-link"><Share2 className="w-4 h-4" />Share link</Button>
                <Button variant="outline" className="gap-2" onClick={() => download("agency")} data-testid="export-agency-csv"><Download className="w-4 h-4" />Export CSV</Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <Metric icon={Users} label="New Leads" value={agency?.totals.leads} accent="#6366F1" testid="agency-leads" />
                <Metric icon={DollarSign} label="Revenue (Won)" value={money(agency?.totals.revenue)} accent="#10B981" testid="agency-revenue" />
                <Metric icon={CalendarCheck} label="Appointments" value={agency?.totals.appointments} accent="#06B6D4" testid="agency-appts" />
                <Metric icon={TrendingUp} label="Show-up Rate" value={`${agency?.totals.show_rate}%`} accent="#F59E0B" testid="agency-showrate" />
              </div>
              <Card className="bg-card border-border/70 overflow-x-auto" data-testid="agency-table">
                <table className="w-full text-sm">
                  <thead className="text-muted-foreground border-b border-border/60">
                    <tr className="[&>th]:text-left [&>th]:px-4 [&>th]:py-3 [&>th]:font-semibold [&>th]:text-xs [&>th]:uppercase [&>th]:tracking-wider">
                      <th>Sub-account</th><th>Leads</th><th>Revenue</th><th>Pipeline</th><th>Won</th><th>Appts</th><th>Show %</th><th>Opt-in %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {agency?.sub_accounts.map((s) => {
                      const m = s.metrics;
                      return (
                        <tr key={s.id} className="[&>td]:px-4 [&>td]:py-3" data-testid={`agency-row-${s.id}`}>
                          <td className="font-medium flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.branding?.primary_color || "#6366F1" }} />{s.name}
                          </td>
                          <td>{m.leads}</td><td>{money(m.deals.revenue)}</td><td>{money(m.deals.pipeline_value)}</td>
                          <td>{m.deals.won}</td><td>{m.appointments.total}</td><td>{m.appointments.show_rate}%</td><td>{m.funnels.opt_in_rate}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="client" className="mt-6 space-y-6">
            {!client ? <Card className="p-16 text-center text-muted-foreground bg-card border-border/70">Select a sub-account to view its report.</Card> : (
              <>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" className="gap-2" onClick={() => shareLink("client")} data-testid="share-client-link"><Share2 className="w-4 h-4" />Share link</Button>
                  <Button variant="outline" className="gap-2" onClick={() => download("client")} data-testid="export-client-csv"><Download className="w-4 h-4" />Export CSV</Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                  <Metric icon={Users} label="New Leads" value={client.metrics.leads} accent="#6366F1" testid="client-leads" />
                  <Metric icon={DollarSign} label="Revenue (Won)" value={money(client.metrics.deals.revenue)} accent="#10B981" testid="client-revenue" />
                  <Metric icon={Target} label="Open Pipeline" value={money(client.metrics.deals.pipeline_value)} accent="#8B5CF6" testid="client-pipeline" />
                  <Metric icon={TrendingUp} label="Win Rate" value={`${client.metrics.deals.win_rate}%`} accent="#F59E0B" testid="client-winrate" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="p-6 bg-card border-border/70" data-testid="client-appointments">
                    <div className="flex items-center gap-2 mb-5"><CalendarCheck className="w-4 h-4 text-primary" /><h3 className="font-display font-semibold text-lg">Appointments</h3></div>
                    <div className="grid grid-cols-2 gap-4">
                      {[["Booked", client.metrics.appointments.booked, "#06B6D4"], ["Completed", client.metrics.appointments.completed, "#10B981"],
                        ["No-shows", client.metrics.appointments.no_show, "#F43F5E"], ["Cancelled", client.metrics.appointments.cancelled, "#94A3B8"]].map(([l, v, c]) => (
                        <div key={l} className="flex items-center gap-3">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />
                          <span className="text-sm text-muted-foreground flex-1">{l}</span>
                          <span className="font-display font-bold">{v}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 pt-4 border-t border-border/60 flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Show-up rate</span>
                      <span className="font-display font-bold text-lg" style={{ color: brand }}>{client.metrics.appointments.show_rate}%</span>
                    </div>
                  </Card>

                  <Card className="p-6 bg-card border-border/70" data-testid="client-funnels">
                    <div className="flex items-center gap-2 mb-5"><Layout className="w-4 h-4 text-primary" /><h3 className="font-display font-semibold text-lg">Funnels</h3></div>
                    {client.funnels.length === 0 ? <p className="text-sm text-muted-foreground">No funnels yet.</p> : (
                      <div className="space-y-3">
                        {client.funnels.map((f, i) => (
                          <div key={i} className="flex items-center gap-3 text-sm" data-testid={`funnel-row-${i}`}>
                            <span className="flex-1 truncate font-medium">{f.name}</span>
                            <span className="text-muted-foreground">{f.visits} visits</span>
                            <span className="text-muted-foreground">{f.submissions} opt-ins</span>
                            <span className="font-semibold w-12 text-right" style={{ color: brand }}>{f.opt_in_rate}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                </div>

                {client.pipelines.map((p, i) => (
                  <Card key={i} className="p-6 bg-card border-border/70" data-testid={`pipeline-report-${i}`}>
                    <div className="flex items-center gap-2 mb-5"><BarChart3 className="w-4 h-4 text-primary" /><h3 className="font-display font-semibold text-lg">{p.name} · {p.total} deals</h3></div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {p.stages.map((st, k) => (
                        <div key={k} className="rounded-lg border border-border/60 p-3">
                          <p className="text-xs text-muted-foreground truncate">{st.name}</p>
                          <p className="font-display font-bold text-xl mt-1">{st.count}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
